import { z } from 'zod'

import type { RequestService } from '../request'
import { managedSchema } from '@scalar-org/entities/managed-schema'

/**
 * API for interacting with managed schemas
 *
 * This is a wrapper around the core/managed-schemas endpoints in the core service
 */
export function managedSchemasApiFactory(requestService: RequestService) {
  /** Get a list of all schemas for the namespace */
  async function getSchemas(namespace: string) {
    return await requestService.request({
      url: `core/schemas/${namespace}`,
      method: 'get',
      schema: managedSchema
        .pick({
          uid: true,
          title: true,
          description: true,
          slug: true,
          namespace: true,
          isPrivate: true,
          versions: true,
        })
        .array(),
    })
  }

  /** Create a new managed schema */
  async function create(
    namespace: string,
    data: {
      title: string
      description?: string
      version: string
      slug: string
      isPrivate?: boolean
      document?: string
    },
  ) {
    return await requestService.request({
      url: `core/schemas/${namespace}`,
      method: 'post',
      data,
      schema: z.object({ uid: z.string() }),
    })
  }

  /** Update an existing managed schema */
  async function update(
    namespace: string,
    slug: string,
    data: Partial<{
      namespace: string
      slug: string
      title: string
      description: string
      isPrivate: boolean
    }>,
  ) {
    return await requestService.request({
      url: `core/schemas/${namespace}/${slug}`,
      method: 'PATCH',
      data,
      schema: z.null(),
    })
  }

  /** Delete an existing managed schema */
  async function del(namespace: string, slug: string) {
    return await requestService.request({
      url: `core/schemas/${namespace}/${slug}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  /** Get a managed schema document contents */
  async function get(namespace: string, slug: string, version: string) {
    return await requestService.request({
      url: `core/schemas/${namespace}/${slug}/version/${version}`,
      method: 'get',
      schema: z.string(),
    })
  }

  /** Create a new version of the managed schema */
  async function createVersion(
    namespace: string,
    slug: string,
    data: {
      version: string
      document: string
    },
  ) {
    return await requestService.request({
      url: `core/schemas/${namespace}/${slug}/version`,
      method: 'post',
      data,
      schema: z.object({ uid: z.string() }),
    })
  }

  /** Update managed doc version contents */
  async function updateVersion(
    namespace: string,
    slug: string,
    version: string,
    data: { content: string },
  ) {
    return await requestService.request({
      url: `core/schemas/${namespace}/${slug}/version/${version}`,
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  /** Update managed doc version */
  async function deleteVersion(
    namespace: string,
    slug: string,
    version: string,
  ) {
    return await requestService.request({
      url: `core/schemas/${namespace}/${slug}/version/${version}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  /** Check if the slug is available */
  async function isRegistrySlugAvailable(namespace: string, slug: string) {
    return await requestService.request({
      url: 'core/schemas/check-slug',
      method: 'post',
      data: {
        namespace,
        slug,
      },
      schema: z.object({
        available: z.boolean(),
      }),
    })
  }

  /** Add an access group to the managed schema */
  async function addAccessGroup(data: {
    namespace: string
    slug: string
    groupUid: string
  }) {
    return await requestService.request({
      url: 'core/schemas/access-group',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  /** Remove an access group to the managed schema */
  async function removeAccessGroup(data: {
    namespace: string
    slug: string
    groupUid: string
  }) {
    return await requestService.request({
      url: 'core/schemas/access-group',
      method: 'delete',
      data,
      schema: z.null(),
    })
  }

  return {
    getSchemas,
    create,
    update,
    delete: del,

    get,
    createVersion,
    updateVersion,
    deleteVersion,

    isRegistrySlugAvailable,

    addAccessGroup,
    removeAccessGroup,
  }
}
